import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { TENANTS_INFO } from '@fitness-force/state';
import { BlackoutConfigService, ToastMessageService } from '@fitness-force/api';
import type { BlackoutConfig } from '@fitness-force/models';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { BlackoutPeriodCreateComponent } from '../blackout-period-create/blackout-period-create.component';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { EntityStatusComponent, YesNoComponent } from '@fitness-force/shared';

// Using relative imports within the same library to avoid circular dependencies
@Component({
  selector: 'blackout-period-list',
  templateUrl: './blackout-period-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    AsyncPipe,
    FormsModule,
    ButtonModule,
    DynamicDialogModule,
    RippleModule,
    SelectModule,
    SkeletonModule,
    TableModule,
    DataGridComponent,
    TranslateModule,
    BlackoutPeriodCreateComponent,
  ],
})
export class BlackoutPeriodListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly blackoutConfigService = inject(BlackoutConfigService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(DynamicDialogRef);

  tenantId = signal(0);
  createNew = signal(false);
  showCreateDialog = signal(false);
  activeEditConfig = signal<BlackoutConfig | null>(null);

  // Filter state
  filterType = signal<'Date' | 'Time' | null>(null);
  filterStatus = signal<boolean | null>(null);

  typeFilterOptions = [
    { label: 'All', value: null },
    { label: 'Date', value: 'Date' },
    { label: 'Time', value: 'Time' },
  ];

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  loading = new BehaviorSubject<boolean>(false);
  tableData = new BehaviorSubject<BlackoutConfig[]>([]);
  columns = new BehaviorSubject<unknown[]>([]);
  totalRecordLength = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 1,
    reset: false,
  });
  emptyStateComponent$ = new BehaviorSubject<unknown>(NoComponentComponent);
  selectAllData = new BehaviorSubject(false);

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: any) => {
      this.tenantId.set(tenant.selected_tenant.id);
      if (this.tenantId() > 0) {
        this.loadList();
        this.buildColumns();
      }
    });
  }

  loadList(): void {
    this.loading.next(true);
    const filters: { type?: 'Date' | 'Time'; is_active?: boolean } = {};
    const type = this.filterType();
    const status = this.filterStatus();
    if (type !== null) filters.type = type;
    if (status !== null) filters.is_active = status;

    this.blackoutConfigService.listBlackoutConfigs(this.tenantId(), filters).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : [];
        this.tableData.next(items);
        this.totalRecordLength.next({
          totalPage: 1,
          totalItems: items.length,
          currentPage: 1,
          reset: false,
        });
        this.loading.next(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.tableData.next([]);
        this.loading.next(false);
        this.cdr.detectChanges();
      },
    });
  }

  onTypeFilterChange(value: 'Date' | 'Time' | null): void {
    this.filterType.set(value);
    this.loadList();
  }

  onStatusFilterChange(value: boolean | null): void {
    this.filterStatus.set(value);
    this.loadList();
  }

  get hasActiveFilters(): boolean {
    return this.filterType() !== null || this.filterStatus() !== null;
  }

  clearFilters(): void {
    this.filterType.set(null);
    this.filterStatus.set(null);
    this.loadList();
  }

  buildColumns(): void {
    this.translate
      .get([
        'CAMPAIGN.BLACKOUT.NAME',
        'CAMPAIGN.BLACKOUT.TYPE',
        'CAMPAIGN.BLACKOUT.START_DATE',
        'CAMPAIGN.BLACKOUT.END_DATE',
        'CAMPAIGN.BLACKOUT.RECURRING',
        'CAMPAIGN.BLACKOUT.STATUS',
      ])
      .subscribe((t) => {
        this.columns.next([
          {
            title: t['CAMPAIGN.BLACKOUT.NAME'].toUpperCase(),
            classes: 'py-2 white-space-nowrap font-bold',
            sortable: true,
            filterable: true,
            dataProperty: 'name_pri_lang',
            type: 'default',
          },
          {
            title: t['CAMPAIGN.BLACKOUT.TYPE'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'type',
            type: 'default',
          },
          {
            title: t['CAMPAIGN.BLACKOUT.START_DATE'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: false,
            dataProperty: 'start_date',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: { KeyName: 'start_date', format: 'd MMM y' },
            },
          },
          {
            title: t['CAMPAIGN.BLACKOUT.END_DATE'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: false,
            dataProperty: 'end_date',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: { KeyName: 'end_date', format: 'd MMM y' },
            },
          },
          {
            title: t['CAMPAIGN.BLACKOUT.RECURRING'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: false,
            dataProperty: 'is_recurring',
            type: 'component',
            data: { name: YesNoComponent,
              additionData: true,
              aData: { fetch_key: 'is_recurring', type: 'booleanyesno' },
            },
          },
          {
            title: t['CAMPAIGN.BLACKOUT.STATUS'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: false,
            dataProperty: 'is_active',
            type: 'component',
            data: { name: EntityStatusComponent },
          },
          {
            title: '',
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['edit', 'status'],
            },
          },
        ]);
        this.cdr.detectChanges();
      });
  }

  onActionEvent(event: { action: string; columnData: BlackoutConfig }): void {
    switch (event.action) {
      case 'edit':
        this.activeEditConfig.set(event.columnData);
        this.createNew.set(false);
        this.showCreateDialog.set(true);
        break;
      case 'status':
        this.toggleStatus(event.columnData);
        break;
    }
  }

  toggleStatus(config: BlackoutConfig): void {
    const call$ = config.is_active
      ? this.blackoutConfigService.markBlackoutInactive(this.tenantId(), config.config_id)
      : this.blackoutConfigService.markBlackoutActive(this.tenantId(), config.config_id);

    const msgKey = config.is_active
      ? 'CAMPAIGN.BLACKOUT.DEACTIVATED_SUCCESS'
      : 'CAMPAIGN.BLACKOUT.ACTIVATED_SUCCESS';

    call$.subscribe({
      next: () => {
        this.translate.get(msgKey).subscribe((msg) => {
          this.toastMessageService.showSuccess(msg);
        });
        //this.loadList();
        this.tableData.next(this.tableData.value.map(item => (item.config_id === config.config_id ? { ...item, is_active: !item.is_active } : item)));
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.toastMessageService.showError(err);
        this.cdr.detectChanges();
      },
    });
  }

  changedConfig(): void {
    this.createNew.set(false);
    this.showCreateDialog.set(false);
    this.activeEditConfig.set(null);
    this.loadList();
  }

  closeCreate(): void {
    this.createNew.set(false);
    this.showCreateDialog.set(false);
    this.activeEditConfig.set(null);
  }

  openCreate(): void {
    this.activeEditConfig.set(null);
    this.createNew.set(true);
    this.showCreateDialog.set(true);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
